import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Form, Field } from 'react-final-form';
import { makeStyles } from '@material-ui/core';

import { Button, Grid, units } from '@ha/dsl';
import { useIntl } from 'ha/i18n';
import Track from 'ha/components/track/SmartTrack';

import LocationsInput from './LocationsInput';
import { submitSearch } from '../../actions';
import { getInitialValues } from '../../selectors';
import DateRangePicker from './DateRangePicker';
import validateForm from './validateForm';

const useStyles = makeStyles(() => ({
  container: {
    padding: units.yx(0.25, 0.5),
  },
  button: {
    background: 'linear-gradient(to right, #b56da8, #8e5082)',
    boxShadow: 'inherit',
    '&:hover': {
      boxShadow: '0 2px 9px 0 rgba(146, 83, 134, 0.35)',
    },
    height: units.rem(3.5),
    fontSize: units.rem(1.125),
    fontWeight: 'bold',
  },
}));

export interface FormValues {
  place: string;
  dateRange: {
    startDate?: string | null;
    endDate?: string | null;
  };
}

export const SearchBar: React.FC = () => {
  const { T, lang, urlResolver } = useIntl();
  const classes = useStyles();
  const dispatch = useDispatch();
  const initialValues = useSelector(getInitialValues);

  const onSubmit = React.useCallback((formValues: FormValues) => {
    dispatch(submitSearch({ formValues, lang, urlResolver }));
  }, []);

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={initialValues}
      validate={values => validateForm(values, T)}
    >
      {({ handleSubmit, submitting, values }) => (
        <form onSubmit={handleSubmit}>
          <Grid container spacing={1} className={classes.container}>
            <Grid item xs={12} md={6}>
              <Field
                name="place"
                id="place"
                component={LocationsInput}
                placeholder={T('Where will you go?')}
                predictionTypes={['(cities)']}
                dataTestLocator="Location Input Box"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Field name="dateRange" component={DateRangePicker} />
            </Grid>

            <Grid item xs={12} md={2}>
              <Track
                name="Clicked Search button"
                type="button"
                getDynamicData={() => values}
              >
                <Button
                  disabled={submitting}
                  type="submit"
                  fullWidth
                  data-test-locator="Search and book"
                  variant="contained"
                  color="primary"
                  classes={{
                    root: classes.button,
                  }}
                >
                  {T('Search')}
                </Button>
              </Track>
            </Grid>
          </Grid>
        </form>
      )}
    </Form>
  );
};

export default SearchBar;
